import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MatDialogRef } from "@angular/material/dialog";
import { ApiService } from "src/app/services/api.service";
import endpoints from "src/app/helpers/endpoints";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { ErrorType } from "src/app/enums/error-type.enum";
import { environment } from "src/environments/environment";
import { MapsService } from "src/app/services/maps.service";

@Component({
  selector: "fury-add-copay",
  templateUrl: "./add-copay.component.html",
  styleUrls: ["./add-copay.component.scss"],
})
export class AddCopayComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("billingLocation", { static: false })
  public billingLocation!: ElementRef;
  ngUnsubscribe = new Subject<void>();
  month = Number(new Date().getMonth() + 1);
  expiryYear: number = Number(new Date().getFullYear().toString().slice(2, 4));
  chargeCopayForm = new FormGroup({
    firstName: new FormControl("", [
      Validators.required,
      Validators.minLength(2),
    ]),
    lastName: new FormControl("", [
      Validators.required,
      Validators.minLength(2),
    ]),
    emailAddress: new FormControl("", [
      Validators.required,
      Validators.pattern(/^[\w.-]+@[\w.-]+\.\w{2,3}$/),
    ]),
    phoneNumber: new FormControl("", [
      Validators.required,
      Validators.pattern(/^[2-9][0-8]\d{1}[2-9]\d{2}\d{4}$/),
    ]),
    claimNumber: new FormControl("", [Validators.required]),
    copayAmount: new FormControl("", [Validators.required]),
    cardNumber: new FormControl("", [Validators.required]),
    expiryMonth: new FormControl("", [
      Validators.required,
      Validators.max(12),
      Validators.min(1),
    ]),
    expiryYear: new FormControl("", [
      Validators.required,
      Validators.min(this.expiryYear),
    ]),
    cvv: new FormControl("", [Validators.required]),
    monthYear: new FormControl("", [Validators.required]),
    streetAddress: new FormControl("", [Validators.required]),
    city: new FormControl("", [Validators.required]),
    state: new FormControl("", [Validators.required]),
    zip: new FormControl("", [Validators.required]),
    unitNumber: new FormControl(""),
  });
  cardType = "";
  assetsUrl = environment.assetsUrl + "images/";

  isLoading = false;
  constructor(
    public dialogRef: MatDialogRef<AddCopayComponent>,
    public apiService: ApiService,
    private mapsService: MapsService
  ) {}

  ngOnInit(): void {
    this.chargeCopayForm
      .get("monthYear")
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: string) => {
        if (res) {
          if (res.length === 4) {
            this.chargeCopayForm.patchValue({
              expiryMonth: res.substring(0, 2),
              expiryYear: res.substring(2, 4),
            });
            if (
              this.chargeCopayForm.get("expiryYear").value ===
                this.expiryYear.toString() &&
              Number(this.chargeCopayForm.get("expiryMonth").value) < this.month
            ) {
              this.chargeCopayForm
                .get("expiryMonth")
                .setErrors({ notValid: true });
            }
          }
        }
      });
      this.chargeCopayForm
      .get("streetAddress")
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: string) => {
        if(!res){
          this.chargeCopayForm.get('state').setValue('');
          this.chargeCopayForm.get('city').setValue('');
          this.chargeCopayForm.get('zip').setValue('');
        }
      });
  }

  close() {
    this.mapsService.setAddressEvent(null);
    this.dialogRef.close();
  }
  detectCardType(cardNumber: string) {
    this.cardType = this.getCardType(cardNumber);
  }
  getCardType(cardNumber: string): string {
    const cardNumberDigitsOnly = cardNumber.replace(/\D/g, "");
    const firstDigit = cardNumberDigitsOnly.charAt(0);
    if (firstDigit === "4") {
      return "Visa";
    } else if (firstDigit === "5") {
      return "Mastercard";
    } else if (firstDigit === "3") {
      return "American Express";
    } else if (firstDigit === "6") {
      return "Discover";
    } else {
      return "Unknown";
    }
  }
  chargeCopay() {
    if (this.chargeCopayForm.valid) {
      const payload = this.createPayload();
      this.isLoading = true;
      this.apiService
        .post(endpoints.chargeCopay.chargeCopay, payload)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (res: HttpResponse<any>) => {
            if (res.body) {
              this.mapsService.setAddressEvent(null);
              this.apiService.alert(res.body?.message, ErrorType.Success);
              this.isLoading = false;
              this.dialogRef.close(res.body);
            }
          },
          error: (err: HttpErrorResponse) => {
            this.isLoading = false;
            this.apiService.alert(err.message, ErrorType.Error);
          },
        });
    } else {
      this.chargeCopayForm.markAllAsTouched();
    }
  }
  createPayload() {
    const payload = {
      firstName: this.chargeCopayForm.value.firstName.trim(),
      lastName: this.chargeCopayForm.value.lastName.trim(),
      claimNumber: this.chargeCopayForm.value.claimNumber,
      copaymentFee: this.chargeCopayForm.value.copayAmount,
      emailAddress: this.chargeCopayForm.value.emailAddress,
      phoneNumber: this.chargeCopayForm.value.phoneNumber,
      cardDetail: {
        cardNumber: this.chargeCopayForm.value.cardNumber,
        expiryMonth: this.chargeCopayForm.value.expiryMonth,
        expiryYear: `20${this.chargeCopayForm.value.expiryYear}`,
        cvv: this.chargeCopayForm.value.cvv,
      },
      billingAddress: {
        streetAddress: this.chargeCopayForm.value.streetAddress.split(',')[0],
        city: this.chargeCopayForm.value.city,
        state: this.chargeCopayForm.value.state,
        zip: this.chargeCopayForm.value.zip,
        unitNumber: this.chargeCopayForm.value.unitNumber,
      },
    };
    return payload;
  }
  claimNumberValidation(event: any): boolean {
    let status = true;
    const regex = new RegExp("^[0-9/]*$");
    const key = String.fromCharCode(
      !event.charCode ? event.which : event.charCode
    );
    if (!regex.test(key)) {
      event.preventDefault();
      status = false;
    } else {
      // Check if "/" is already present
      const currentValue = event.target.value || "";
      const slashCount = (currentValue.match(/\//g) || []).length;

      if (key === "/" && slashCount >= 1) {
        event.preventDefault();
        status = false;
      }
    }
    return status;
  }
  ngAfterViewInit() {
    setTimeout(() => {
      if (this.billingLocation)
        this.mapsService.autoFillLocation(
          this.billingLocation,
          "billingLocation"
        );
    }, 200);

    this.mapsService.getaddressEvent
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        if (res && typeof res === "object" && res.root === "billingLocation") {
          this.chargeCopayForm.patchValue({
            streetAddress: res?.address,
            state: res?.city,
            city: res?.state,
            zip: res?.zipcode,
          });
        }
      });
  }
  checkZipCode(event: any) {
    if (this.chargeCopayForm?.value?.zip?.length !== 5) {
      return;
    }
    this.apiService
      .getCityState(this.chargeCopayForm.controls["zip"])
      .then((data) => {
        if (data) {
          this.chargeCopayForm.patchValue(data);
        } else {
          this.chargeCopayForm.controls["zip"].setErrors({ notValid: true });
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
